// Cola de ventas offline (modo offline — Fase 2).
//
// Cuando el cajero cobra sin internet, la venta NO se pierde: se guarda aquí y
// se reenvía sola cuando vuelve la conexión. El reenvío usa la RPC
// `mezo_venta_directa` (migración 0032), que es IDEMPOTENTE por client_local_id
// → aunque la red flaquee a mitad del reenvío, nunca se cobra dos veces.
//
// Usamos localStorage (no IndexedDB): una cafetería offline una hora genera
// decenas de ventas pequeñas, no miles; localStorage es síncrono, simple y
// confiable para ese volumen, y se vacía apenas vuelve el internet.
//
// La cola está AISLADA POR NEGOCIO: en un dispositivo compartido (logout de A,
// login de B) las ventas de A no se intentan subir con la sesión de B (la RPC
// las rechazaría con 'No autorizado' y quedarían atascadas para siempre).

import { getBusinessId } from './supabaseClient';

const PREFIJO   = 'mezo_cola_ventas_';
const PREFIJO_F = 'mezo_ventas_fallidas_';
const MAX_INTENTOS = 5; // tras N fallos NO de red, se aparta para revisión

function _key()  { return `${PREFIJO}${getBusinessId() || 'anon'}`; }
function _keyF() { return `${PREFIJO_F}${getBusinessId() || 'anon'}`; }

function _leer(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
}
function _guardar(key, arr) {
  try { localStorage.setItem(key, JSON.stringify(arr)); } catch { /* cuota / privado */ }
}

// ── Pub/sub para que la UI muestre el contador de pendientes en vivo ─────────
const _subs = new Set();
export function onColaCambio(cb) { _subs.add(cb); return () => _subs.delete(cb); }
function _notificar() { _subs.forEach((cb) => { try { cb(contarPendientes()); } catch { /* */ } }); }

// ── API de la cola ──────────────────────────────────────────────────────────
export function encolarVenta(venta) {
  const cola = _leer(_key());
  cola.push(venta);
  _guardar(_key(), cola);
  _notificar();
  return venta;
}

export function ventasPendientes() { return _leer(_key()); }
export function contarPendientes() { return _leer(_key()).length; }
export function ventasFallidas()   { return _leer(_keyF()); }

function _eliminar(localId) {
  _guardar(_key(), _leer(_key()).filter((v) => v.localId !== localId));
  _notificar();
}

function _moverAFallidas(venta) {
  const f = _leer(_keyF());
  f.push({ ...venta, fallidaEn: new Date().toISOString() });
  _guardar(_keyF(), f);
  _eliminar(venta.localId);
}

function _esErrorDeRed(e) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const m = (e?.message || '').toLowerCase();
  return m.includes('failed to fetch') || m.includes('networkerror')
      || m.includes('network request failed') || m.includes('load failed');
}

// Reenvía la cola del negocio actual. `enviar` sube una venta al servidor (la
// inyecta api.js para no crear dependencia circular). Devuelve {ok, fail}.
let _sincronizando = false;
export async function sincronizar(enviar) {
  if (_sincronizando) return { ok: 0, fail: 0 };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: 0, fail: 0 };

  _sincronizando = true;
  let ok = 0, fail = 0;
  try {
    for (const venta of _leer(_key())) {
      try {
        await enviar(venta);
        _eliminar(venta.localId);   // la RPC es idempotente: seguro eliminar
        ok++;
      } catch (e) {
        fail++;
        // Sin red: paramos; reintentamos en la próxima vuelta (no cuenta intento).
        if (_esErrorDeRed(e)) break;
        // Error NO de red (datos raros, permisos): subimos el contador. Tras
        // MAX_INTENTOS la apartamos a "fallidas" para que no bloquee el resto
        // ni deje el banner "sincronizando" colgado para siempre.
        const intentos = (venta.intentos ?? 0) + 1;
        if (intentos >= MAX_INTENTOS) {
          _moverAFallidas(venta);
        } else {
          const cola = _leer(_key()).map((v) =>
            v.localId === venta.localId ? { ...v, intentos } : v);
          _guardar(_key(), cola);
        }
      }
    }
  } finally {
    _sincronizando = false;
  }
  return { ok, fail };
}
